/**
 * India Timezone Configuration for Frontend
 * 
 * This file ensures consistent India Standard Time (IST) handling
 * in the React frontend application.
 * 
 * India Standard Time: UTC+5:30
 * Timezone: Asia/Kolkata
 */

// 🎯 INDIA TIMEZONE CONFIGURATION
const INDIA_TIMEZONE = {
  // India Standard Time configuration
  timezone: 'Asia/Kolkata',
  offset: '+05:30', // UTC+5:30
  displayName: 'India Standard Time (IST)',
  
  // Business hours in IST
  businessHours: {
    start: '09:00',
    end: '18:00'
  }
};

/**
 * Get current date/time in India Standard Time
 * @returns {Date} Current date in IST
 */
export const getIndiaTime = () => {
  const now = new Date();
  // Convert to IST (UTC+5:30)
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  return istTime;
};

/**
 * Get current date/time as ISO string in IST
 * @returns {string} ISO string in IST
 */
export const getIndiaTimeISO = () => {
  const istTime = getIndiaTime();
  return istTime.toISOString();
};

/**
 * Format date to India timezone
 * @param {Date|string} date - Date to format
 * @param {string} format - Format type: 'display', 'database', 'api', 'relative', 'time-only'
 * @returns {string} Formatted date string
 */
export const formatIndiaTime = (date, format = 'display') => {
  try {
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    // Handle different timestamp formats
    let istTime;
    if (typeof date === 'string' && date.includes('Z')) {
      // API timestamp with 'Z' suffix but actually in IST timezone
      // Remove 'Z' and treat as local IST time
      const istString = date.replace('Z', '');
      istTime = new Date(istString);
    } else if (typeof date === 'string' && !date.includes('Z') && !date.includes('T')) {
      // Database timestamp (like '2025-09-16 18:58:00') - treat as IST
      const [datePart, timePart] = date.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hours, minutes, seconds] = timePart.split(':');
      istTime = new Date(year, month - 1, day, hours, minutes, seconds);
    } else {
      // Local timestamp or Date object - use as is
      istTime = dateObj;
    }
    
    const year = istTime.getFullYear();
    const month = String(istTime.getMonth() + 1).padStart(2, '0');
    const day = String(istTime.getDate()).padStart(2, '0');
    const hours = String(istTime.getHours()).padStart(2, '0');
    const minutes = String(istTime.getMinutes()).padStart(2, '0');
    const seconds = String(istTime.getSeconds()).padStart(2, '0');
    
    switch (format) {
      case 'display':
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} IST`;
      case 'database':
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      case 'api':
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+05:30`;
      case 'relative':
        return getRelativeTime(istTime);
      case 'time-only':
        return `${hours}:${minutes}`;
      case 'time-ampm':
        const hour12 = istTime.getHours() % 12 || 12;
        const ampm = istTime.getHours() >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      default:
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} IST`;
    }
  } catch (error) {
    console.error('Error formatting India time:', error);
    return 'Invalid Date';
  }
};

/**
 * Get relative time (Today, Yesterday, or date)
 * @param {Date} istTime - Date in IST
 * @returns {string} Relative time string
 */
const getRelativeTime = (istTime) => {
  const now = new Date();
  
  // Get dates without time (set to midnight) for comparison
  const messageDate = new Date(istTime.getFullYear(), istTime.getMonth(), istTime.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Compare dates
  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    // For older dates, show the date
    const day = String(istTime.getDate()).padStart(2, '0');
    const month = String(istTime.getMonth() + 1).padStart(2, '0');
    const year = istTime.getFullYear();
    return `${day}/${month}/${year}`;
  }
};

/**
 * Convert UTC date to India time
 * @param {Date|string} utcDate - UTC date
 * @returns {Date} Date in IST
 */
export const utcToIndiaTime = (utcDate) => {
  const date = new Date(utcDate);
  return new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
};

/**
 * Convert India time to UTC
 * @param {Date|string} indiaDate - India time date
 * @returns {Date} Date in UTC
 */
export const indiaTimeToUTC = (indiaDate) => {
  const date = new Date(indiaDate);
  return new Date(date.getTime() - (5.5 * 60 * 60 * 1000));
};

/**
 * Check if current time is within India business hours
 * @returns {boolean} True if within business hours
 */
export const isIndiaBusinessHours = () => {
  const now = getIndiaTime();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeMinutes = currentHour * 60 + currentMinute;
  
  const [startHour, startMinute] = INDIA_TIMEZONE.businessHours.start.split(':').map(Number);
  const [endHour, endMinute] = INDIA_TIMEZONE.businessHours.end.split(':').map(Number);
  
  const startTimeMinutes = startHour * 60 + startMinute;
  const endTimeMinutes = endHour * 60 + endMinute;
  
  return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes <= endTimeMinutes;
};

/**
 * Get timezone info for debugging
 * @returns {object} Timezone information
 */
export const getTimezoneInfo = () => {
  const now = new Date();
  const istTime = getIndiaTime();
  
  return {
    utc: now.toISOString(),
    ist: istTime.toISOString(),
    istFormatted: formatIndiaTime(now, 'display'),
    offset: INDIA_TIMEZONE.offset,
    timezone: INDIA_TIMEZONE.timezone,
    isBusinessHours: isIndiaBusinessHours()
  };
};

/**
 * Compare two dates in IST
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareIndiaTime = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getTime() - d2.getTime();
};

/**
 * Check if a date is in the past (in IST)
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastIndiaTime = (date) => {
  const targetDate = new Date(date);
  const now = new Date();
  return targetDate.getTime() < now.getTime();
};

// Export configuration
export const config = {
  ...INDIA_TIMEZONE,
  getIndiaTime,
  getIndiaTimeISO,
  formatIndiaTime,
  utcToIndiaTime,
  indiaTimeToUTC,
  isIndiaBusinessHours,
  getTimezoneInfo,
  getRelativeTime,
  compareIndiaTime,
  isPastIndiaTime
};

export default config;
